import { Modal, Checkbox, FormControlLabel } from "@mui/material";
import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFileContract,
  faChevronDown,
  faCheck,
  faXmark,
  faCircleCheck,
} from "@fortawesome/free-solid-svg-icons";
import ActionButton from "../ui/ActionButton";
import "../../style/TermsAndConditions.css";

// simple terms and conditions modal that requires scrolling to the bottom of the T&Cs before accepting and proceeding.
function TermsModal({ open, onClose, onAccept, onDecline, darkMode }) {
  const { t } = useTranslation();
  const [isChecked, setIsChecked] = useState(false);
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  const termsRef = useRef(null);
  const scrollEnabled = useRef(false);

  // reset state and scroll position when modal opens
  useEffect(() => {
    if (open) {
      setHasScrolledToBottom(false);
      setIsChecked(false);
      scrollEnabled.current = false;
      // reset scroll position and enable detection after layout settles
      requestAnimationFrame(() => {
        if (termsRef.current) {
          termsRef.current.scrollTop = 0;
        }
        // delay enabling scroll detection to avoid layout-triggered false positives
        setTimeout(() => {
          scrollEnabled.current = true;
        }, 300);
      });
    }
  }, [open]);

  // check if user has scrolled to bottom
  const handleScroll = () => {
    if (!scrollEnabled.current) return;
    if (termsRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = termsRef.current;
      const isAtBottom = scrollTop + clientHeight >= scrollHeight - 20;

      if (isAtBottom && !hasScrolledToBottom) {
        setHasScrolledToBottom(true);
      }
    }
  };

  const handleAccept = () => {
    onAccept();
  };

  const handleCheckboxChange = (e) => {
    setIsChecked(e.target.checked);
  };

  return (
    <Modal open={open} onClose={onClose} aria-labelledby="terms-modal">
      <div className={`terms-modal ${darkMode ? "dark-mode" : "light-mode"}`}>
        {/* Header */}
        <div className="terms-header">
          <div className="terms-header-icon">
            <FontAwesomeIcon icon={faFileContract} />
          </div>
          <h1 className="terms-title">{t("Terms and Conditions")}</h1>
          <p className="terms-subtitle">
            {t("Please review and accept our terms to continue")}
          </p>
        </div>

        {/* Divider */}
        <div className="terms-divider"></div>

        {/* Scroll instruction */}
        {!hasScrolledToBottom && (
          <div className="terms-scroll-hint">
            <FontAwesomeIcon
              icon={faChevronDown}
              className="terms-scroll-icon"
            />
            <span>{t("Please scroll down to read all terms")}</span>
            <FontAwesomeIcon
              icon={faChevronDown}
              className="terms-scroll-icon"
            />
          </div>
        )}

        {/* Scrolled indicator */}
        {hasScrolledToBottom && (
          <div className="terms-scrolled-indicator">
            <FontAwesomeIcon icon={faCircleCheck} />
            <span>{t("All terms reviewed")}</span>
          </div>
        )}

        {/* Scrollable T&C content */}
        <div className={`terms-content ${hasScrolledToBottom ? "footer-visible" : ""}`} ref={termsRef} onScroll={handleScroll}>
          <div className="terms-section">
            <div className="terms-section-number">1</div>
            <div className="terms-section-body">
              <h3 className="terms-section-title">{t("Lorem Ipsum Dolor")}</h3>
              <p className="terms-section-text">
                Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do
                eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut
                enim ad minim veniam, quis nostrud exercitation ullamco laboris
                nisi ut aliquip ex ea commodo consequat.
              </p>
            </div>
          </div>

          <div className="terms-section">
            <div className="terms-section-number">2</div>
            <div className="terms-section-body">
              <h3 className="terms-section-title">{t("Duis Aute Irure")}</h3>
              <p className="terms-section-text">
                Duis aute irure dolor in reprehenderit in voluptate velit esse
                cillum dolore eu fugiat nulla pariatur. Excepteur sint occaecat
                cupidatat non proident, sunt in culpa qui officia deserunt
                mollit anim id est laborum.
              </p>
            </div>
          </div>

          <div className="terms-section">
            <div className="terms-section-number">3</div>
            <div className="terms-section-body">
              <h3 className="terms-section-title">
                {t("Sed Perspiciatis Unde")}
              </h3>
              <p className="terms-section-text">
                Sed ut perspiciatis unde omnis iste natus error sit voluptatem
                accusantium doloremque laudantium, totam rem aperiam, eaque ipsa
                quae ab illo inventore veritatis et quasi architecto beatae
                vitae dicta sunt explicabo.
              </p>
            </div>
          </div>

          <div className="terms-section">
            <div className="terms-section-number">4</div>
            <div className="terms-section-body">
              <h3 className="terms-section-title">{t("Nemo Enim Ipsam")}</h3>
              <p className="terms-section-text">
                Nemo enim ipsam voluptatem quia voluptas sit aspernatur aut odit
                aut fugit, sed quia consequuntur magni dolores eos qui ratione
                voluptatem sequi nesciunt. Neque porro quisquam est, qui dolorem
                ipsum quia dolor sit amet.
              </p>
            </div>
          </div>

          <div className="terms-section">
            <div className="terms-section-number">5</div>
            <div className="terms-section-body">
              <h3 className="terms-section-title">{t("Temporibus Autem")}</h3>
              <p className="terms-section-text">
                At vero eos et accusamus et iusto odio dignissimos ducimus qui
                blanditiis praesentium voluptatum deleniti atque corrupti quos
                dolores et quas molestias excepturi sint occaecati cupiditate
                non provident.
              </p>
            </div>
          </div>

          <div className="terms-section">
            <div className="terms-section-number">6</div>
            <div className="terms-section-body">
              <h3 className="terms-section-title">{t("Nam Libero Tempore")}</h3>
              <p className="terms-section-text">
                Nam libero tempore, cum soluta nobis est eligendi optio cumque
                nihil impedit quo minus id quod maxime placeat facere possimus,
                omnis voluptas assumenda est, omnis dolor repellendus.
                Temporibus autem quibusdam et aut officiis debitis.
              </p>
            </div>
          </div>
        </div>

        {/* Footer — only visible after scrolling to bottom */}
        {hasScrolledToBottom && (
          <div className="terms-footer-reveal">
            {/* Checkbox */}
            <div className="terms-checkbox-wrapper terms-stagger-1">
              <FormControlLabel
                control={
                  <Checkbox
                    checked={isChecked}
                    onChange={handleCheckboxChange}
                    sx={{
                      color: "var(--login-accent)",
                      "&.Mui-checked": {
                        color: "var(--login-accent)",
                      },
                    }}
                  />
                }
                label={
                  <span className="terms-checkbox-label">
                    {t("I have read and accept the terms and conditions")}
                  </span>
                }
              />
            </div>

            {/* Buttons */}
            <div className="terms-actions">
              <div className="terms-stagger-2">
                <ActionButton
                  onClick={handleAccept}
                  disabled={!isChecked}
                  variant="secondary"
                  icon={faCheck}
                  modal
                  className={isChecked ? "accept-ready" : ""}
                >
                  {t("Accept")}
                </ActionButton>
              </div>
              <div className="terms-stagger-3">
                <ActionButton
                  onClick={onDecline}
                  variant="danger"
                  icon={faXmark}
                  modal
                >
                  {t("Decline")}
                </ActionButton>
              </div>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}

export default TermsModal;
